import {Platform} from 'react-native';
import * as Sentry from '@sentry/react';
import {reaction} from 'mobx';

import Config from '../config.generated';
import type {RootStore} from '../stores/rootStore';
import dev from '../utils/dev';

class SentryService {
  private initialized = false;
  private disposers: (() => void)[] = [];

  /**
   * Initialize Sentry - call once at app startup
   * Only enables on web platform when sentryEnabled flag is true
   */
  async initialize(rootStore: RootStore, sentryEnabled: boolean): Promise<void> {
    // Only for web platform
    if (Platform.OS !== 'web' || this.initialized || !sentryEnabled) {
      return;
    }

    // Skip if no DSN configured
    if (!Config.SENTRY_DSN) {
      return;
    }

    const environment = dev.enableDebugging() ? 'development' : 'production';

    Sentry.init({
      dsn: Config.SENTRY_DSN,
      environment,
      release: `frontend-web@${Config.APP_VERSION || '0.0.0'}`,
      // Only capture errors, not transactions (performance)
      tracesSampleRate: 0,
      // Filter out noisy errors
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Network request failed',
        'Load failed',
        'Failed to fetch',
      ],
    });

    // Set brand tag
    Sentry.setTag('brand', Config.BRAND_ID || 'unknown');

    this.initialized = true;
    this.setupUserTracking(rootStore);
    this.setupContextTracking(rootStore);
  }

  private setupUserTracking(rootStore: RootStore): void {
    this.disposers.push(
      reaction(
        () => rootStore.userStore.id,
        userId => {
          if (userId) {
            Sentry.setUser({id: userId});
          } else {
            Sentry.setUser(null);
          }
        },
        {fireImmediately: true},
      ),
    );
  }

  private setupContextTracking(rootStore: RootStore): void {
    this.disposers.push(
      reaction(
        () => rootStore.shopSelectionStore.selectedStoreSummary?.id,
        storeId => {
          if (storeId) {
            Sentry.setTag('storeId', storeId);
          } else {
            Sentry.setTag('storeId', '');
          }
        },
        {fireImmediately: true},
      ),
    );
  }

  /**
   * Capture an exception and send to Sentry
   */
  captureException(error: Error, context?: string): void {
    if (!this.initialized) return;
    Sentry.captureException(error, context ? {tags: {context}} : undefined);
  }

  /**
   * Clean up MobX reactions
   */
  dispose(): void {
    this.disposers.forEach(dispose => dispose());
    this.disposers = [];
  }
}

export const sentryService = new SentryService();
